import { Component, HostListener, inject } from '@angular/core';
import { NavigationError, Router, RouterOutlet } from '@angular/router';
import { UserService } from './core/services/user.service';
import { DeployVersionService } from './core/services/deploy-version.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent {
  // Eagerly instantiate UserService so GetOneUser::REQ is sent on first WS connection
  private readonly _userService = inject(UserService);
  // Eagerly instantiate so its baseline main.js fingerprint reflects what this page actually
  // loaded, not whatever happens to be live on the server the first time a tab fails to load.
  private readonly _deployVersion = inject(DeployVersionService);
  private readonly router = inject(Router);

  constructor() {
    // Route-level `loadComponent` chunks fail the same way `@defer` chunks do (see
    // tab-loading-state.component.ts) when a deploy has swapped in freshly-hashed files that a
    // stale tab still references, but the Router reports it as a NavigationError instead of an
    // `@defer` `@error` block, so it needs the same reload-if-actually-redeployed check here.
    this.router.events.subscribe((event) => {
      if (!(event instanceof NavigationError) || !this.isChunkLoadError(event.error)) return;
      this._deployVersion.isNewVersionDeployed().then((isNew) => {
        if (isNew) window.location.reload();
      });
    });
  }

  private isChunkLoadError(error: unknown): boolean {
    const message = error instanceof Error ? error.message : String(error);
    return /Failed to fetch dynamically imported module|ChunkLoadError|Importing a module script failed/i.test(
      message,
    );
  }

  /**
   * Prevents accidental value changes in number inputs when scrolling.
   * If the wheel event occurs on a number input, the input is blurred.
   * @param event The wheel event from the document
   */
  @HostListener('document:wheel', ['$event'])
  onDocumentWheel(event: WheelEvent): void {
    const target = event.target as HTMLInputElement;
    if (target.tagName === 'INPUT' && target.type === 'number') {
      target.blur();
    }
  }
}
